// src/hooks/useSubscription.js
//
// Thin derivation over useAuth: turns `company.subscription` + billing capability
// into the flags the UI gates on. The clamp itself lives in useAuth (it must run
// there to feed `hasPermission`); this hook just exposes the read-only view.
//
// `canManageBilling` comes from useAuth (derived off RAW, pre-clamp permissions).
// See PRD/PRD_Billing.md + PRD/PRD_BillingPhase2.md (Step 9 — dashboard cards).
import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';
import {
  getSubscriptionPhase,
  isValidPhase,
  getDaysLeft,
  PHASE,
} from '../config/subscription';
import { PERMISSIONS } from '../config/permissions';
import { computeBillEstimate } from '../config/plans';

export const useSubscription = () => {
  const { company, canManageBilling, hasPermission } = useAuth();
  const subscription = company?.subscription || null;
  const canViewBilling =
    typeof hasPermission === 'function' && hasPermission(PERMISSIONS.SETTINGS_PAYMENTS_VIEW);

  // Active-employee counter (H1a). Read ONLY when the caller can view billing — the
  // Firestore rule gates counters/{id} reads to set:bill:view, so a non-billing user
  // would just permission-error. `null` = unknown/not-permitted (cards fall back to the
  // subscription-doc telemetry alone). A one-shot getDoc (not onSnapshot): useSubscription
  // is instantiated in several components at once (banner, layout, page, cards), and
  // multiple persistent listeners on the same doc + StrictMode churn trips a known
  // firebase-js-sdk "INTERNAL ASSERTION FAILED" bug. The count changes rarely; it
  // refreshes when company.id changes (navigation) — good enough, no live listener.
  const companyId = company?.id || null;
  const [activeCount, setActiveCount] = useState(null);
  useEffect(() => {
    if (!companyId || !canViewBilling) {
      setActiveCount(null);
      return undefined;
    }
    let cancelled = false;
    getDoc(doc(db, 'companies', companyId, 'counters', 'employees'))
      .then((snap) => {
        if (!cancelled) setActiveCount(snap.exists() ? Number(snap.data().active) || 0 : 0);
      })
      .catch(() => {
        if (!cancelled) setActiveCount(null); // permission/missing → leave unknown
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, canViewBilling]);

  const phase = getSubscriptionPhase(subscription);
  const isValid = isValidPhase(phase);
  const isExpired = phase === PHASE.EXPIRED;
  const daysLeft = getDaysLeft(subscription, phase);

  // Billing estimate (H1b): the running cycle max is the SERVER-precomputed
  // max(cycleSnapshotMax, liveCount) — never the raw live count alone (else a
  // company that peaked at 30 then dropped to 22 would show a falling estimate even
  // though its next bill is still based on 30). Reads only the subscription doc +
  // the counter — never the activeEmployeeSnapshots collection.
  const cycleSnapshotMax = Number(subscription?.cycleSnapshotMax) || 0;
  const cycleStartCount =
    subscription?.cycleStartCount != null ? Number(subscription.cycleStartCount) : null;
  const runningCycleMax = Math.max(cycleSnapshotMax, activeCount ?? 0);
  // Memoized so it's a stable reference across renders (safe to key effects/memos
  // off it later; today no consumer does, so this is just a latent-footgun guard).
  const billingEstimate = useMemo(() => computeBillEstimate(runningCycleMax), [runningCycleMax]);

  return {
    status: subscription?.status || null,
    phase,
    isValid,
    isExpired,
    canManageBilling,
    canViewBilling,
    // Owner-only read-only clamp vs. non-owner hard block (both only when expired)
    isReadOnly: isExpired && canManageBilling,
    isBlocked: isExpired && !canManageBilling,
    daysLeft,
    trialEnd: subscription?.trialEnd || null,
    graceEndsAt: subscription?.graceEndsAt || null,
    // ── Phase 2 billing-card fields (purely additive; existing consumers ignore) ──
    activeCount, // live counter, or null when unknown/not-permitted
    cycleStartCount, // count at cycle start (H2 net-change), or null
    runningCycleMax, // max(cycleSnapshotMax, liveCount) — the estimate input
    billingEstimate, // computeBillEstimate(runningCycleMax) → {total, base, overage, breakdown}
    stripeCustomerId: subscription?.stripeCustomerId || null,
    stripeSubscriptionId: subscription?.stripeSubscriptionId || null,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd || false,
    currentPeriodEnd: subscription?.currentPeriodEnd || null, // renewal date for active subs
  };
};
